import * as Sentry from '@sentry/angular';
import { ErrorEvent } from '@sentry/angular';

import { IDENTITY_IS_SUPPORT_ACCESS, scrubSentryEvent, SENTRY_IGNORE_ERRORS } from '@efficiently/models';
import { Mut } from '@efficiently/shared-util-types';

import { scrubSentryLog } from './sentry-log-scrub.util';
import { type OpenFeatureHookRegistrar, registerOpenFeatureSentryAttribution } from './sentry-openfeature.util';

const SENTRY_REPLAY_SAMPLE_RATE = 0.05;
const SENTRY_TRACES_SAMPLE_RATE = 0.05;
const SENTRY_PROFILES_SAMPLE_RATE = 0.02;
const SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE = 0.1;
const SENTRY_REPLAY_CANVAS_MAX_SIZE = [640, 640] as const;
const KONVA_STAGE_LAYERS_WARNING_REGEXP = /^Konva warning: The stage has \d+ layers/;
const APOLLO_MESSAGE_REGEXP = /^Apollo\./;

/**
 * Depth at which the view-hierarchy walk stops descending.
 *
 * The integration walks the whole DOM synchronously for every error event, and
 * the schedule grid nests deeply enough for an unbounded walk to be noticeable.
 * The top levels carry the diagnostic value — which page, dialog and panel were
 * on screen — so the deep leaves are pruned.
 */
const SENTRY_VIEW_HIERARCHY_MAX_DEPTH = 20;

/**
 * Name fragments whose values must not reach Sentry — the SDK's own legacy
 * `sendDefaultPii: false` deny list. Secret-like names (`auth`, `token`,
 * `password`, `key`, `session`) are filtered by the SDK unconditionally.
 */
const SENTRY_PII_DENY_TERMS: string[] = ['forwarded', '-ip', 'remote-', 'via', '-user'];

interface ClientSentryBuildConfig {
	readonly release: string;
	readonly commitId: string;
	readonly sentryDist: string;
	readonly replaysSampleRate: number | string | undefined;
	readonly replaysOnErrorSampleRate: number | string | undefined;
	readonly tracesSampleRate: number | string | undefined;
	readonly profilesSampleRate: number | string | undefined;
	/** Build-time twin of the backend's `SENTRY_LOG_ENABLED`; absent means off. */
	readonly sentryLogsEnabled?: boolean;
}

interface ClientSentryEnvironment {
	readonly name: string;
	readonly endpoints: {
		readonly sentryDsn: string;
	};
	readonly build: ClientSentryBuildConfig;
}

export interface ClientSentryOptions {
	readonly app: string;
	readonly environment: ClientSentryEnvironment;
	readonly windowRef?: Window;
	/**
	 * The OpenFeature global API — `OpenFeature` from `@openfeature/web-sdk` — for feature-flag
	 * attribution (BR-4).
	 *
	 * Optional because an application may have no flag consumer at all (`client-portal` has none today),
	 * and because this library must not depend on a flag SDK to be initialised. **Omitting it ships blank
	 * attribution**: the flag section of an event is simply absent, silently, which is exactly the failure
	 * `registerOpenFeatureSentryAttribution` documents. An application that registers a provider must
	 * pass it here in the same breath.
	 */
	readonly openFeature?: OpenFeatureHookRegistrar;
}

function getSampleRate(value: number | string | undefined, fallback: number): number {
	if (typeof value === 'number') {
		return value;
	}

	if (typeof value === 'string' && value !== '') {
		const parsed = Number(value);

		return Number.isNaN(parsed) ? fallback : parsed;
	}

	return fallback;
}

/**
 * Attaches feature-flag attribution when the application wired OpenFeature, and does nothing when it did
 * not (BR-4).
 *
 * A named function rather than an `if` inside `initClientSentry`: the guard is the whole content of the
 * decision, and stating it here keeps the one place that decides whether attribution exists readable —
 * `initClientSentry` is already a long function whose branch count is watched.
 */
function registerFlagAttribution(openFeature: OpenFeatureHookRegistrar | undefined): void {
	if (!openFeature) {
		return;
	}

	registerOpenFeatureSentryAttribution(openFeature);
}

export function isClientSentryEnabled(options: Pick<ClientSentryOptions, 'environment' | 'windowRef'>): boolean {
	const windowRef = options.windowRef ?? window;

	return Boolean(options.environment.endpoints.sentryDsn) && windowRef.location.hostname !== 'localhost';
}

export function initClientSentry(options: ClientSentryOptions): void {
	const windowRef = options.windowRef ?? window;

	if (!isClientSentryEnabled({ environment: options.environment, windowRef })) {
		return;
	}

	const isSupportLoggedIn = Boolean(windowRef.localStorage.getItem(IDENTITY_IS_SUPPORT_ACCESS));
	const replaysSessionSampleRate = getSampleRate(
		options.environment.build.replaysSampleRate,
		SENTRY_REPLAY_SAMPLE_RATE,
	);
	const replaysOnErrorSampleRate = getSampleRate(
		options.environment.build.replaysOnErrorSampleRate,
		SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
	);
	const tracesSampleRate = getSampleRate(options.environment.build.tracesSampleRate, SENTRY_TRACES_SAMPLE_RATE);
	const profilesSampleRate = getSampleRate(options.environment.build.profilesSampleRate, SENTRY_PROFILES_SAMPLE_RATE);

	Sentry.init({
		dsn: options.environment.endpoints.sentryDsn,
		environment: options.environment.name,
		release: options.environment.build.release,
		dist: options.environment.build.sentryDist,
		replaysOnErrorSampleRate,
		replaysSessionSampleRate,
		tracesSampler: samplingContext => {
			if (samplingContext.request?.url?.includes('fonts.gstatic.com')) {
				return 0;
			}

			if (samplingContext.parentSampled !== undefined) {
				return samplingContext.parentSampled ? tracesSampleRate : 0;
			}

			return tracesSampleRate;
		},
		profilesSampleRate,
		initialScope: {
			tags: {
				app: options.app,
				commit_id: options.environment.build.commitId,
				environment: options.environment.name,
				release: options.environment.build.release,
			},
		},
		ignoreErrors: SENTRY_IGNORE_ERRORS,
		// Opt-in: forwarding client log records multiplies what we send, so it is rolled out per
		// environment rather than switched on everywhere at once.
		enableLogs: options.environment.build.sentryLogsEnabled === true,
		// Logs bypass `beforeSend`, so they need their own scrubber.
		beforeSendLog: log => scrubSentryLog(log),
		// Replaces `sendDefaultPii`, deprecated in SDK 10.57 and gone in v11. Every
		// category is listed on purpose: the moment `dataCollection` is present the SDK
		// stops using its legacy "pii off" bridge and applies its own defaults, where
		// `userInfo`, `cookies`, `httpHeaders`, `httpBodies` and `databaseQueryData` are
		// all ON. A partial object here would widen what we send, so each key pins
		// today's behaviour. `userInfo: false` only blocks SDK-inferred user fields —
		// what `AppConfigService` passes to `setUser` is always sent.
		dataCollection: {
			userInfo: false,
			cookies: { deny: SENTRY_PII_DENY_TERMS },
			httpHeaders: { request: { deny: SENTRY_PII_DENY_TERMS }, response: { deny: SENTRY_PII_DENY_TERMS } },
			httpBodies: [],
			urlQueryParams: { deny: SENTRY_PII_DENY_TERMS },
			graphQL: { document: true, variables: false },
			genAI: { inputs: false, outputs: false },
			databaseQueryData: false,
			stackFrameVariables: true,
			frameContextLines: 7,
		},
		beforeSend(event: Mut<ErrorEvent>) {
			const sessionId = Sentry.getIsolationScope()?.getSession?.()?.sid;
			const message = event.message ?? '';

			event.tags = {
				...event.tags,
				...(sessionId ? { session_id: String(sessionId) } : {}),
				...(isSupportLoggedIn ? { is_support_logged_in: 'true' } : {}),
			};

			if (KONVA_STAGE_LAYERS_WARNING_REGEXP.test(message)) {
				return null;
			}

			if (APOLLO_MESSAGE_REGEXP.test(message)) {
				return null;
			}

			return scrubSentryEvent(event);
		},
		tracePropagationTargets: [/^https:\/\/.*\.app\.efficiently\.com\//, 'https://app.efficiently.com'],
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.browserProfilingIntegration(),
			// `page` keeps one session per real page load instead of starting a new one
			// on every in-app navigation, which is what a router-driven SPA needs for
			// Crash Free Sessions to mean anything. The integration starts and captures
			// the session itself — do not call `startSession()` alongside it.
			Sentry.browserSessionIntegration({ lifecycle: 'page' }),
			Sentry.contextLinesIntegration(),
			Sentry.extraErrorDataIntegration({
				depth: 5,
			}),
			Sentry.replayIntegration({
				maskAllText: false,
				blockAllMedia: false,
				ignore: ['.ds-sentry-ignore, [ds-data-sentry-ignore]', 'input[type="password"]'],
				block: ['.ds-sentry-block', '[ds-data-sentry-block]', '.sensitive-data'],
				networkDetailAllowUrls: [],
				networkRequestHeaders: [],
			}),
			Sentry.replayCanvasIntegration({
				quality: 'low',
				maxCanvasSize: SENTRY_REPLAY_CANVAS_MAX_SIZE,
			}),
			Sentry.graphqlClientIntegration({
				endpoints: [/\/graphql$/],
			}),
			// Attaches the DOM structure at the moment of an error: which page, dialog
			// and panel were on screen. The attachment holds structure only — tag /
			// component name, element id and geometry — never text or attribute
			// values, so it needs no masking of its own.
			Sentry.viewHierarchyIntegration({
				onElement: ({ depth }) =>
					depth !== undefined && depth >= SENTRY_VIEW_HIERARCHY_MAX_DEPTH ? 'skip' : {},
			}),
			// The `http.client` span ends when response headers arrive, which hides how
			// long a streamed body actually took. This adds an `http.client.stream`
			// sibling span covering header arrival through body completion.
			Sentry.fetchStreamPerformanceIntegration(),
		],
		maxBreadcrumbs: 50,
	});

	// After `init`, never before: the integration needs a client to attach to, and hooks that recorded
	// onto a scope predating one would be recording into something already discarded.
	registerFlagAttribution(options.openFeature);
}

export function suppressConsoleInProduction(): void {
	// Production builds intentionally silence informational console output to
	// keep the DevTools console clean for end users. `console.error` is kept so
	// bootstrap and runtime failures remain visible.
	/* eslint-disable no-console */
	console.log = (): void => {};
	console.info = (): void => {};
	console.warn = (): void => {};
	console.debug = (): void => {};
	/* eslint-enable no-console */
}
